import {
  createContext,
  useContext,
  useState,
  FC,
  useCallback,
  ReactNode,
} from 'react';
import { Snackbar, Box, IconButton } from '@mui/joy';
import { WarningSvg } from '../../components/Infra/Icons/WarningSvg';
import { InfoSvg } from '../../components/Infra/Icons/InfoSvg';
import { CloseSvg } from '../../components/Infra/Icons/CloseSvg';
import {
  SnackbarColor,
  SnackbarContextProps,
  SnackbarOptions,
  SnackbarProviderProps,
  SnackbarType,
} from './Types';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
const SnackbarContext = createContext<SnackbarContextProps | undefined>(
  undefined
);

let showSnackbarGlobal: (options: SnackbarOptions) => void;

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must backend used within a SnackbarProvider');
  }
  return context;
};

const defaultSnackbarState = {
  title: '',
  message: '',
  icon: <></>,
  color: SnackbarColor.neutral,
  open: false,
  timeout: 0,
  action: null as ReactNode,
};

export const SnackbarProvider: FC<SnackbarProviderProps> = ({ children }) => {
  const [snackbar, setSnackbar] = useState(defaultSnackbarState);

  const showSnackbar = useCallback(
    ({ title, message, type, timeout = 4000, action }: SnackbarOptions) => {
      let icon: ReactNode = <></>;
      let color: SnackbarColor = SnackbarColor.neutral;

      if (type === SnackbarType.error) {
        icon = <WarningSvg sx={{ mb: 3, color: '#C32518' }} />;
        color = SnackbarColor.danger;
      } else if (type === SnackbarType.info) {
        icon = <InfoSvg sx={{ mb: 3, color: '#1A73E8' }} />;
      } else if (type === SnackbarType.warning) {
        icon = <ReportGmailerrorredIcon color="warning" sx={{ mb: 3 }} />;
        color = SnackbarColor.warning;
      } else if (type === SnackbarType.success) {
        icon = <ReportGmailerrorredIcon color="success" sx={{ mb: 3 }} />;
        color = SnackbarColor.success;
      }

      setSnackbar({ title, message, icon, color, timeout, open: true, action });
    },
    []
  );

  showSnackbarGlobal = showSnackbar;

  const handleClose = (closeReason: string) => {
    if (closeReason === 'timeout') {
      setSnackbar(prev => ({ ...prev, open: false }));
    }
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      <Snackbar
        data-testid="snackbar"
        open={snackbar.open}
        autoHideDuration={snackbar.timeout}
        color={snackbar.color}
        onClose={(_, closeReason: string) => handleClose(closeReason)}
        size="md"
        variant="outlined"
        startDecorator={snackbar.icon}
        endDecorator={
          <IconButton
            sx={{
              mb: 3,
            }}
            color={snackbar.color}
            onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            <CloseSvg />
          </IconButton>
        }
        sx={{ minWidth: '350px', borderRadius: '12px' }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}
        >
          <Box sx={{ fontSize: '20px', fontWeight: 500 }}>{snackbar.title}</Box>
          <Box sx={{ fontSize: '16px', fontWeight: 400 }}>
            {snackbar.message}
          </Box>
          {snackbar.action && <Box sx={{ mt: 1 }}>{snackbar.action}</Box>}
        </Box>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

export const showSnackbar = (options: SnackbarOptions) =>
  showSnackbarGlobal(options);
